import raw from "./data/catalogue.json";
import { parseCatalogue } from "./data/schema";
import { applyDocumentLang, saveLang, type Lang } from "./i18n/index";
import { View } from "./render/index";
import { Clock, SPEED_PRESETS } from "./sim/clock";
import { SolarSystem } from "./sim/solar-system";
import { mountBodiesTab } from "./ui/bodies-tab";
import { mountControls } from "./ui/controls";
import { mountInfoChip } from "./ui/info";
import { showFatal } from "./ui/fatal";
import { mountHelp } from "./ui/help";
import { bindKeys } from "./ui/keyboard";
import { LabelLayer, selectLabels, type LabelInput } from "./ui/labels";
import { mountLoading } from "./ui/loading";
import { mountMenu } from "./ui/menu";
import { isClick } from "./ui/pointer";
import { browserAddress, createHashSync } from "./ui/hash-sync";
import { Store } from "./ui/store";
import { FrameMeter, formatStats, mountStats } from "./ui/stats";
import { mountTimePill } from "./ui/time-pill";
import { formatViewport, mountViewportProbe } from "./ui/viewport";

export interface AppHandle {
  bodyCount: number;
  drawCalls(): number;
  indexOf(id: string): number;
  store: Store;
  clock: Clock;
  view: View;
}

export function startApp(
  canvas: HTMLCanvasElement,
  labelsContainer: HTMLElement,
  overlay: HTMLElement,
  lang: Lang,
): AppHandle {
  const bodies = parseCatalogue(raw);
  const sim = new SolarSystem(bodies);
  const { table } = sim;
  const clock = new Clock();
  const store = new Store();
  // before any module mounts, so each draws its text once in the right language
  store.set({ lang });
  const view = new View(canvas, sim);
  const ids = new Set(bodies.map((b) => b.id));

  // before any module mounts, so each starts from the address's focus and scale
  const hashSync = createHashSync({ store, clock, ids, address: browserAddress(window) });

  const info = mountInfoChip(overlay, store, (id) => {
    store.set({ menuOpen: true, menuTab: "bodies" });
    bodiesTab.reveal(id);
  });
  const pill = mountTimePill(overlay, {
    clock,
    store,
    onOpenControls: () => {
      store.set({ menuOpen: true, menuTab: "controls" });
      // move focus into the menu so Esc and M reach its own key handler
      menu.focusActiveTab();
    },
  });
  const goHome = (): void => {
    store.set({ focus: "sun" });
    // when the Sun is already focused the store does not notify; reset the zoom anyway
    view.setFocus(table.root);
  };
  const menu = mountMenu(overlay, pill.el, store);
  const controls = mountControls(menu.panels.controls, {
    clock,
    store,
    onNow: () => clock.setNow(),
    onReset: () => {
      goHome();
      // like choosing a body: the sheet covers the scene, so close it to show the flight
      if (menu.isSheet()) store.set({ menuOpen: false });
    },
  });
  const stats = mountStats(overlay, store);
  const viewport = mountViewportProbe(overlay, canvas);
  const loading = mountLoading(overlay, store);
  const selectBody = (id: string): void => {
    if (store.state.focus === id) view.setFocus(table.index.get(id) ?? table.root);
    else store.set({ focus: id });
  };
  const bodiesTab = mountBodiesTab(menu.panels.bodies, {
    bodies,
    store,
    onSelect: (id) => {
      selectBody(id);
      // the sheet covers the scene; close it so the camera flight is visible
      if (menu.isSheet()) store.set({ menuOpen: false });
    },
  });
  mountHelp(menu.panels.help, store);
  const labels = new LabelLayer(labelsContainer, bodies, selectBody);
  labels.setLang(store.state.lang);

  const labelInput: LabelInput = {
    count: sim.count,
    screen: view.screen,
    tiers: table.tier,
    parentIndex: table.parentIndex,
    radiusKm: table.radiusKm,
    focus: 0,
    distance: new Float32Array(sim.count),
    parentRadius: view.frameData.radii,
    shown: view.frameData.shown,
    width: 1,
    height: 1,
    touchTargets: matchMedia("(pointer: coarse)").matches,
  };
  const labelVisible = new Uint8Array(sim.count);

  const applyFocus = (id: string): void => {
    const index = table.index.get(id) ?? table.root;
    view.setFocus(index);
    const parent = table.parentIndex[index];
    info.show(bodies[index], parent >= 0 ? bodies[parent] : null, table.moonCount[index]);
  };
  let lastFocus = store.state.focus;
  let lastScale = store.state.scale;
  let lastLang = store.state.lang;
  store.subscribe((s) => {
    if (s.lang !== lastLang) {
      lastLang = s.lang;
      // only a choice made in the menu is stored; until then the browser language decides
      saveLang(s.lang);
      applyDocumentLang(s.lang);
      labels.setLang(s.lang);
    }
    if (s.focus !== lastFocus) {
      lastFocus = s.focus;
      applyFocus(s.focus);
    }
    if (s.scale !== lastScale) {
      lastScale = s.scale;
      view.setScaleMode(s.scale);
    }
    view.setOrbitsVisible(s.orbits);
    stats.setVisible(s.stats);
  });
  applyFocus(store.state.focus);
  view.setScaleMode(store.state.scale);
  view.setOrbitsVisible(store.state.orbits);
  stats.setVisible(store.state.stats);

  const stepSpeed = (dir: 1 | -1): void => {
    const k = SPEED_PRESETS.findIndex((p) => p.secondsPerSecond >= clock.speed);
    const next = Math.min(
      Math.max((k < 0 ? SPEED_PRESETS.length - 1 : k) + dir, 0),
      SPEED_PRESETS.length - 1,
    );
    clock.speed = SPEED_PRESETS[next].secondsPerSecond;
  };
  bindKeys(window, {
    togglePause: () => {
      clock.paused = !clock.paused;
    },
    stepSpeed,
    toggleScale: () => store.set({ scale: store.state.scale === "true" ? "visual" : "true" }),
    toggleLabels: () => store.set({ labels: !store.state.labels }),
    toggleOrbits: () => store.set({ orbits: !store.state.orbits }),
    home: goHome,
    escape: () => {
      if (store.state.menuOpen) store.set({ menuOpen: false });
      else if (store.state.infoExpanded) store.set({ infoExpanded: false });
      else goHome();
    },
    toggleStats: () => store.set({ stats: !store.state.stats }),
    toggleMenu: () => {
      store.set({ menuOpen: !store.state.menuOpen });
      if (store.state.menuOpen) menu.focusActiveTab();
    },
    openSearch: () => {
      store.set({ menuOpen: true, menuTab: "bodies" });
      bodiesTab.focusSearch();
    },
    openHelp: () => {
      store.set({ menuOpen: true, menuTab: "help" });
      menu.focusActiveTab();
    },
  });

  let hover = -1;
  canvas.addEventListener("pointermove", (e) => {
    hover = view.pick(e.clientX, e.clientY);
    labels.setHover(hover);
    view.setHover(hover);
    canvas.style.cursor = hover >= 0 ? "pointer" : "";
  });
  let downX = 0,
    downY = 0,
    downPointerType = "";
  canvas.addEventListener("pointerdown", (e) => {
    downX = e.clientX;
    downY = e.clientY;
    downPointerType = e.pointerType;
  });
  canvas.addEventListener("click", (e) => {
    // an orbit drag ends in a click event too; only a press without travel changes focus
    if (!isClick(downX, downY, e.clientX, e.clientY)) return;
    const k = view.pick(e.clientX, e.clientY, downPointerType === "touch" ? 22 : undefined);
    if (k >= 0) selectBody(bodies[k].id);
  });

  const started = performance.now();
  let last = started;
  const meter = new FrameMeter();
  // a hidden tab draws nothing; that gap is not a slow frame
  document.addEventListener("visibilitychange", () => meter.reset());
  const step = (now: number): void => {
    const t0 = performance.now();
    const dt = (now - last) / 1000;
    last = now;
    clock.tick(dt);
    sim.update(clock.jd);
    view.frame(dt);

    labelInput.focus = view.focus;
    labelInput.width = view.viewportWidth();
    labelInput.height = view.viewportHeight();
    for (let k = 0; k < sim.count; k++) labelInput.distance[k] = view.distanceTo(k);
    selectLabels(labelInput, labelVisible);
    labels.update(view.screen, labelVisible, store.state.labels);

    pill.refresh();
    if (store.state.menuOpen && store.state.menuTab === "controls") controls.refresh();
    loading.update(view.textures.completed, view.textures.requested, now - started);
    hashSync.tick(now);
    const timing = meter.sample(now, performance.now() - t0);
    // also while the readout is off, so it has text the moment it is switched on
    if (timing)
      stats.show(
        formatStats(
          {
            ...timing,
            ...view.renderInfo(),
            drawCalls: view.drawCalls(),
            keplerNonConverged: sim.stats.keplerNonConverged,
            nonFinite: sim.stats.nonFinite,
          },
          store.state.lang,
        ) +
          "\n" +
          formatViewport(viewport.measure(), store.state.lang),
      );
  };
  const loop = (now: number): void => {
    try {
      step(now);
    } catch (err) {
      // stop on the first failure instead of throwing every frame behind a frozen canvas
      showFatal(store.state.lang, "renderLoop", err);
      return;
    }
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  return {
    bodyCount: sim.count,
    drawCalls: () => view.drawCalls(),
    indexOf: (id) => table.indexOf(id),
    store,
    clock,
    view,
  };
}
